<template>
    <div class="ai-attr-filter" ref="rootRef">
        <button
            ref="triggerRef"
            type="button"
            class="btn btn-outline-secondary btn-sm mr-2 mb-2 ai-attr-filter__trigger"
            :class="{ active: isOpen }"
            @click="togglePopover"
        >
            <i class="fas fa-plus" aria-hidden="true"></i>
            {{ triggerLabel }}
        </button>

        <input
            v-for="tag in selected"
            :key="`hidden-${tag}`"
            type="hidden"
            name="ai_tags[]"
            :value="tag"
        />
        <input
            v-for="tag in selectedCad"
            :key="`hidden-cad-${tag}`"
            type="hidden"
            name="cad_tags[]"
            :value="tag"
        />
    </div>

    <Teleport to="body">
        <div
            v-show="isOpen"
            ref="popoverRef"
            class="ai-attr-filter__popover"
            role="dialog"
            :aria-label="triggerLabel"
            :style="popoverStyle"
        >
            <div class="ai-attr-filter__cats">
                <button
                    v-for="cat in categories"
                    :key="cat.key"
                    type="button"
                    class="ai-attr-filter__cat"
                    :class="{ 'is-active': cat.key === activeCategory }"
                    @click="switchCategory(cat.key)"
                >
                    <span class="ai-attr-filter__cat-label">{{ cat.label }}</span>
                    <span v-if="cat.selectedCount" class="ai-attr-filter__cat-badge">{{ cat.selectedCount }}</span>
                    <span class="ai-attr-filter__cat-count">{{ cat.count }}</span>
                </button>
            </div>

            <div class="ai-attr-filter__vals">
                <div class="ai-attr-filter__search">
                    <input
                        ref="searchInputRef"
                        v-model="query"
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="searchPlaceholder"
                        autocomplete="off"
                    />
                </div>

                <ul class="ai-attr-filter__list">
                    <li
                        v-for="option in visibleCandidates"
                        :key="option.value"
                        class="ai-attr-filter__row"
                        :class="{ 'is-selected': isSelected(option.value) }"
                        @click="toggleValue(option.value)"
                    >
                        <span class="ai-attr-filter__row-check" aria-hidden="true">
                            <i class="fas" :class="isSelected(option.value) ? 'fa-check-square' : 'fa-square'"></i>
                        </span>
                        <span class="ai-attr-filter__row-label">{{ option.value }}</span>
                        <span class="ai-attr-filter__row-bar" aria-hidden="true">
                            <i :style="{ width: barWidth(option) + '%' }"></i>
                        </span>
                        <span class="ai-attr-filter__row-count">{{ option.count }}</span>
                    </li>
                    <li v-if="activeCandidates.length === 0" class="ai-attr-filter__empty">
                        {{ noCandidatesLabel }}
                    </li>
                    <li v-else-if="filteredCandidates.length === 0" class="ai-attr-filter__empty">
                        {{ noMatchLabel }}
                    </li>
                </ul>

                <div v-if="footerNote" class="ai-attr-filter__footer">
                    {{ footerNote }}
                </div>
            </div>
        </div>
    </Teleport>

    <div v-if="pills.length" class="ai-attr-filter__pills">
        <span
            v-for="pill in pills"
            :key="pill.id"
            class="ai-attr-filter__pill"
        >
            {{ pill.label }}
            <button
                type="button"
                class="ai-attr-filter__pill-remove"
                :aria-label="pill.label"
                @click="removePill(pill)"
            >
                <i class="fas fa-times" aria-hidden="true"></i>
            </button>
        </span>
        <button type="button" class="btn btn-outline-secondary btn-sm ai-attr-filter__clear-all" @click="clearAll">
            {{ clearAllLabel }}
        </button>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import axios from 'axios';

const TOP_LIMIT = 20;

const CATEGORY_KEYS = ['tags', 'scene_tags', 'entities', 'locations', 'persons', 'genres', 'cad_parts', 'cad_materials', 'cad_layers'];

// 図面タグのカテゴリと、cad_tags[] の値の先頭に付ける種類 (VideoRepository の source='cad_tag' の label と同じ)
const CAD_CATEGORY_KINDS = {
    cad_parts: 'part',
    cad_materials: 'material',
    cad_layers: 'layer',
};

// ポップオーバーの位置計算に使う定数。デスクトップ版CSS (max-width: min(600px, calc(100vw - 2rem))) と合わせている。
const POPOVER_DESKTOP_BREAKPOINT = 767.98; // resources/sass/exa/_dashboard.scss のモバイル分岐と同じ値
const POPOVER_WIDTH = 600;
const POPOVER_VIEWPORT_MARGIN = 32; // 2rem 相当
const POPOVER_EDGE_MARGIN = 8;
const POPOVER_GAP = 6;

const parseSelected = (raw) => {
    if (Array.isArray(raw)) {
        return raw.filter((v) => typeof v === 'string' && v !== '');
    }
    if (typeof raw === 'string' && raw.trim() !== '') {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.filter((v) => typeof v === 'string' && v !== '');
            }
        } catch (e) {
            return [];
        }
    }
    return [];
};

const normalizeCandidates = (raw) => {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter((row) => row && typeof row.value === 'string')
        .map((row) => ({ value: row.value, count: Number(row.count) || 0 }));
};

const EMPTY_RAW_DATA = {
    tags: [],
    scene_tags: [],
    persons: [],
    locations: [],
    entities: [],
    genres: [],
    cad_parts: [],
    cad_materials: [],
    cad_layers: [],
};

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// countByCustomer() の応答 ({tags: [...], ...}) を rawData の形に正規化する。
// オブジェクトでなければ (null・集計失敗) 候補なしにする。
const normalizeCountsPayload = (data) => {
    if (!isPlainObject(data)) {
        return { ...EMPTY_RAW_DATA };
    }
    return {
        tags: normalizeCandidates(data.tags),
        scene_tags: normalizeCandidates(data.scene_tags),
        persons: normalizeCandidates(data.persons),
        locations: normalizeCandidates(data.locations),
        entities: normalizeCandidates(data.entities),
        genres: normalizeCandidates(data.genres),
        cad_parts: normalizeCandidates(data.cad_parts),
        cad_materials: normalizeCandidates(data.cad_materials),
        cad_layers: normalizeCandidates(data.cad_layers),
    };
};

// initial_counts prop (String/Object) をパースする。JSON文字列でなければそのまま返す。
// パース不能・"null" 文字列・JSON の null はいずれも null として返し、呼び出し側で候補なし扱いにする。
const parseInitialCounts = (raw) => {
    if (typeof raw !== 'string') {
        return raw ?? null;
    }
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

const interpolate = (template, params) => {
    if (typeof template !== 'string') {
        return '';
    }
    return template.replace(/:(\w+)/g, (matched, key) => (key in params ? String(params[key]) : matched));
};

export default {
    name: 'AiAttributeFilterComponent',
    props: {
        endpoint: {
            type: String,
            required: true,
        },
        selected: {
            type: [String, Array],
            default: '[]',
        },
        selected_cad: {
            type: [String, Array],
            default: '[]',
        },
        initial_counts: {
            type: [String, Object],
        },
        update_event: {
            type: String,
            default: '',
        },
        clear_event: {
            type: String,
            default: 'clear-dashboard-tags',
        },
        trigger_label: {
            type: String,
            required: true,
        },
        search_placeholder: {
            type: String,
            required: true,
        },
        clear_all_label: {
            type: String,
            required: true,
        },
        no_candidates_label: {
            type: String,
            required: true,
        },
        no_match_label: {
            type: String,
            required: true,
        },
        top_limit_note: {
            type: String,
            required: true,
        },
        all_shown_note: {
            type: String,
            required: true,
        },
        label_tags: {
            type: String,
            required: true,
        },
        label_scene_tags: {
            type: String,
            required: true,
        },
        label_entities: {
            type: String,
            required: true,
        },
        label_locations: {
            type: String,
            required: true,
        },
        label_persons: {
            type: String,
            required: true,
        },
        label_genres: {
            type: String,
            required: true,
        },
        label_cad_parts: {
            type: String,
            required: true,
        },
        label_cad_materials: {
            type: String,
            required: true,
        },
        label_cad_layers: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const rootRef = ref(null);
        const triggerRef = ref(null);
        const searchInputRef = ref(null);
        const popoverRef = ref(null);
        const popoverStyle = ref({});

        const rawData = ref({
            tags: [],
            scene_tags: [],
            persons: [],
            locations: [],
            entities: [],
            genres: [],
            cad_parts: [],
            cad_materials: [],
            cad_layers: [],
        });

        const selected = ref(parseSelected(props.selected));
        // 図面タグの選択 (`種類:名前`。種類は part / material / layer)。AI 属性の selected とは別に持つ
        const selectedCad = ref(parseSelected(props.selected_cad));
        const isOpen = ref(false);
        const activeCategory = ref('tags');
        const query = ref('');

        const triggerLabel = props.trigger_label;
        const searchPlaceholder = props.search_placeholder;
        const clearAllLabel = props.clear_all_label;
        const noCandidatesLabel = props.no_candidates_label;

        const categoryLabels = {
            tags: props.label_tags,
            scene_tags: props.label_scene_tags,
            entities: props.label_entities,
            locations: props.label_locations,
            persons: props.label_persons,
            genres: props.label_genres,
            cad_parts: props.label_cad_parts,
            cad_materials: props.label_cad_materials,
            cad_layers: props.label_cad_layers,
        };

        const candidateValueSets = computed(() => {
            const sets = {};
            CATEGORY_KEYS.forEach((key) => {
                sets[key] = new Set(rawData.value[key].map((o) => o.value));
            });
            return sets;
        });

        // 図面タグのカテゴリなら `種類:` の接頭辞を返す。AI 属性のカテゴリは null
        const cadPrefixOf = (key) => (CAD_CATEGORY_KINDS[key] ? `${CAD_CATEGORY_KINDS[key]}:` : null);

        const selectedCountOf = (key) => {
            const prefix = cadPrefixOf(key);
            if (prefix === null) {
                return selected.value.filter((v) => candidateValueSets.value[key].has(v)).length;
            }
            return selectedCad.value.filter(
                (v) => v.startsWith(prefix) && candidateValueSets.value[key].has(v.slice(prefix.length))
            ).length;
        };

        const categories = computed(() =>
            CATEGORY_KEYS.map((key) => ({
                key,
                label: categoryLabels[key],
                count: rawData.value[key].length,
                selectedCount: selectedCountOf(key),
            }))
        );

        const activeCandidates = computed(() => rawData.value[activeCategory.value] || []);

        const filteredCandidates = computed(() => {
            const q = query.value.trim().toLowerCase().normalize('NFC');
            if (q === '') {
                return activeCandidates.value;
            }
            return activeCandidates.value.filter((option) =>
                option.value.toLowerCase().normalize('NFC').includes(q)
            );
        });

        const visibleCandidates = computed(() => filteredCandidates.value.slice(0, TOP_LIMIT));

        const maxCount = computed(() => {
            let max = 0;
            activeCandidates.value.forEach((option) => {
                if (option.count > max) {
                    max = option.count;
                }
            });
            return max;
        });

        const barWidth = (option) => {
            if (!maxCount.value) {
                return 0;
            }
            return Math.round((option.count / maxCount.value) * 100);
        };

        const footerNote = computed(() => {
            if (activeCandidates.value.length === 0 || filteredCandidates.value.length === 0) {
                return '';
            }
            const remaining = filteredCandidates.value.length - TOP_LIMIT;
            if (remaining > 0) {
                return interpolate(props.top_limit_note, { remaining });
            }
            return interpolate(props.all_shown_note, { count: filteredCandidates.value.length });
        });

        // 選択の同一性: AI 属性は値、図面タグは `種類:名前` (同じ名前でも種類が違えば別の選択)
        const isSelected = (value) => {
            const prefix = cadPrefixOf(activeCategory.value);
            return prefix === null ? selected.value.includes(value) : selectedCad.value.includes(prefix + value);
        };

        const toggleValue = (value) => {
            const prefix = cadPrefixOf(activeCategory.value);
            if (prefix === null) {
                selected.value = isSelected(value)
                    ? selected.value.filter((v) => v !== value)
                    : [...selected.value, value];
                return;
            }
            const id = prefix + value;
            selectedCad.value = isSelected(value)
                ? selectedCad.value.filter((v) => v !== id)
                : [...selectedCad.value, id];
        };

        // ピルには名前だけを出す (図面タグは `種類:` を除く)。id は AI 属性と図面タグで衝突しないよう接頭辞を付ける
        const pills = computed(() => [
            ...selected.value.map((v) => ({ id: `ai-${v}`, label: v, cad: false, value: v })),
            ...selectedCad.value.map((v) => ({
                id: `cad-${v}`,
                label: v.slice(v.indexOf(':') + 1),
                cad: true,
                value: v,
            })),
        ]);

        const removePill = (pill) => {
            if (pill.cad) {
                selectedCad.value = selectedCad.value.filter((v) => v !== pill.value);
            } else {
                selected.value = selected.value.filter((v) => v !== pill.value);
            }
        };

        const clearAll = () => {
            selected.value = [];
            selectedCad.value = [];
        };

        const switchCategory = (key) => {
            activeCategory.value = key;
            query.value = '';
            nextTick(() => {
                if (searchInputRef.value) {
                    searchInputRef.value.focus();
                }
            });
        };

        const isDesktopViewport = () => window.innerWidth > POPOVER_DESKTOP_BREAKPOINT;

        // Teleport先(body直下)でのposition:fixed座標をトリガーボタンの位置から算出する。
        // モバイル版はCSSのメディアクエリ(画面下部固定シート)に委ねるため、ここではstyleを空にする。
        const updatePopoverPosition = () => {
            if (!triggerRef.value || !isDesktopViewport()) {
                popoverStyle.value = {};
                return;
            }
            const rect = triggerRef.value.getBoundingClientRect();
            const width = Math.min(POPOVER_WIDTH, window.innerWidth - POPOVER_VIEWPORT_MARGIN);
            const maxLeft = window.innerWidth - width - POPOVER_EDGE_MARGIN;
            const left = Math.max(POPOVER_EDGE_MARGIN, Math.min(rect.left, maxLeft));
            popoverStyle.value = {
                top: `${rect.bottom + POPOVER_GAP}px`,
                left: `${left}px`,
            };
        };

        const onViewportResize = () => {
            if (isOpen.value) {
                updatePopoverPosition();
            }
        };

        // スクロールでトリガーとの位置がずれるケースは再計算せずポップオーバーを閉じる。
        // ただしポップオーバー内部のリスト/カテゴリのスクロール(overflow-y: auto)は対象外にする。
        const onViewportScroll = (event) => {
            if (!isOpen.value) {
                return;
            }
            const target = event.target;
            if (target instanceof Node && popoverRef.value && popoverRef.value.contains(target)) {
                return;
            }
            closePopover();
        };

        const addViewportListeners = () => {
            window.addEventListener('resize', onViewportResize);
            // 内部スクロールコンテナのscrollもcaptureで拾うためtrue指定
            window.addEventListener('scroll', onViewportScroll, true);
        };

        const removeViewportListeners = () => {
            window.removeEventListener('resize', onViewportResize);
            window.removeEventListener('scroll', onViewportScroll, true);
        };

        const openPopover = () => {
            isOpen.value = true;
            updatePopoverPosition();
            addViewportListeners();
            nextTick(() => {
                if (searchInputRef.value) {
                    searchInputRef.value.focus();
                }
            });
        };

        const closePopover = () => {
            isOpen.value = false;
            removeViewportListeners();
        };

        const togglePopover = () => {
            if (isOpen.value) {
                closePopover();
            } else {
                openPopover();
            }
        };

        const onDocumentPointer = (event) => {
            // popoverはTeleportでbody直下に出るためrootRef配下ではない。両方に含まれない場合のみ閉じる。
            const inRoot = rootRef.value && rootRef.value.contains(event.target);
            const inPopover = popoverRef.value && popoverRef.value.contains(event.target);
            if (!inRoot && !inPopover) {
                closePopover();
            }
        };

        const onDocumentKeydown = (event) => {
            if (event.key === 'Escape' && isOpen.value) {
                event.preventDefault();
                closePopover();
                if (triggerRef.value) {
                    triggerRef.value.focus();
                }
            }
        };

        const noMatchLabelResolved = computed(() =>
            interpolate(props.no_match_label, { query: query.value.trim() })
        );

        // 一覧の応答に同梱された件数 (dashboard-ai-attribute-counts) を候補に差し替える。
        // detail がオブジェクトでなければ (null・集計失敗) 候補なしにする。
        const onCountsUpdated = (event) => {
            rawData.value = normalizeCountsPayload(event.detail);
        };

        onMounted(async () => {
            if (props.initial_counts !== undefined) {
                // 一覧の初期表示 (Blade) から件数を受け取り済みのため API は呼ばない
                rawData.value = normalizeCountsPayload(parseInitialCounts(props.initial_counts));
            } else {
                try {
                    const response = await axios.get(props.endpoint);
                    rawData.value = normalizeCountsPayload(response.data);
                } catch (error) {
                    console.error('Error fetching AI attribute candidates:', error);
                }
            }

            document.addEventListener('mousedown', onDocumentPointer);
            document.addEventListener('keydown', onDocumentKeydown);
            document.addEventListener(props.clear_event, clearAll);
            if (props.update_event !== '') {
                document.addEventListener(props.update_event, onCountsUpdated);
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('mousedown', onDocumentPointer);
            document.removeEventListener('keydown', onDocumentKeydown);
            document.removeEventListener(props.clear_event, clearAll);
            if (props.update_event !== '') {
                document.removeEventListener(props.update_event, onCountsUpdated);
            }
            removeViewportListeners();
        });

        return {
            rootRef,
            triggerRef,
            searchInputRef,
            popoverRef,
            popoverStyle,
            selected,
            selectedCad,
            pills,
            isOpen,
            activeCategory,
            query,
            triggerLabel,
            searchPlaceholder,
            clearAllLabel,
            noCandidatesLabel,
            noMatchLabel: noMatchLabelResolved,
            categories,
            activeCandidates,
            filteredCandidates,
            visibleCandidates,
            footerNote,
            barWidth,
            isSelected,
            toggleValue,
            removePill,
            clearAll,
            switchCategory,
            togglePopover,
        };
    },
};
</script>

<style scoped>
.ai-attr-filter {
    position: relative;
    display: inline-block;
}

.ai-attr-filter__trigger {
    white-space: nowrap;
}

.ai-attr-filter__trigger.active {
    border-color: var(--brand-primary, #0d6efd);
    color: var(--brand-primary, #0d6efd);
}

.ai-attr-filter__popover {
    /* Teleport(body直下)先でposition:fixedにし、座標はJS(updatePopoverPosition)で算出する。
       祖先の .content-area { overflow: hidden; } によるクリップを回避するため。 */
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    display: flex;
    width: 600px;
    max-width: min(600px, calc(100vw - 2rem));
    max-height: min(390px, calc(100vh - 56px - var(--dashboard-restore-bar-height, 0px) - 1rem));
    overflow: hidden;
    /* 一覧・カテゴリの端に着いた後のホイールを枠の外(ページ)へ引き継がせない。
       引き継いだ先のscrollはonViewportScrollが枠の外のscrollとして扱い、枠を閉じるため。
       .ai-attr-filter__cats / .ai-attr-filter__list も同じ指定。 */
    overscroll-behavior: contain;
    background: var(--bg-surface, #ffffff);
    border: 1px solid var(--border-default, #ced4da);
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.2);
}

.ai-attr-filter__cats {
    flex: 0 0 190px;
    width: 190px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0.4rem;
    background: var(--bg-canvas, #f8fafc);
    border-right: 1px solid var(--border-default, #ced4da);
}

.ai-attr-filter__cat {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    width: 100%;
    padding: 0.4rem 0.5rem;
    margin-bottom: 0.15rem;
    font-size: 0.8rem;
    color: var(--text-main, #495057);
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 0.375rem;
    cursor: pointer;
}

.ai-attr-filter__cat:hover {
    background: var(--bg-surface, #ffffff);
}

.ai-attr-filter__cat.is-active {
    color: var(--brand-primary, #0d6efd);
    font-weight: 700;
    background: color-mix(in srgb, var(--brand-primary, #0d6efd) 12%, transparent);
}

.ai-attr-filter__cat-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ai-attr-filter__cat-badge {
    flex: 0 0 auto;
    min-width: 1.1rem;
    padding: 0 0.3rem;
    font-size: 0.68rem;
    line-height: 1.4rem;
    text-align: center;
    color: #fff;
    background: var(--brand-primary, #0d6efd);
    border-radius: 999px;
}

.ai-attr-filter__cat-count {
    flex: 0 0 auto;
    margin-left: auto;
    font-size: 0.7rem;
    color: var(--text-main, #495057);
    opacity: 0.6;
}

.ai-attr-filter__vals {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
}

.ai-attr-filter__search {
    padding: 0.5rem 0.6rem 0.4rem;
    border-bottom: 1px solid var(--border-default, #ced4da);
}

.ai-attr-filter__list {
    flex: 1;
    margin: 0;
    padding: 0.3rem;
    overflow-y: auto;
    overscroll-behavior: contain;
    list-style: none;
}

.ai-attr-filter__row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.5rem;
    font-size: 0.8rem;
    color: var(--text-main, #495057);
    cursor: pointer;
    border-radius: 0.375rem;
}

.ai-attr-filter__row:hover {
    background: var(--bg-canvas, #f8fafc);
}

.ai-attr-filter__row.is-selected {
    color: var(--brand-primary, #0d6efd);
    font-weight: 700;
    background: color-mix(in srgb, var(--brand-primary, #0d6efd) 10%, transparent);
}

.ai-attr-filter__row-check {
    flex: 0 0 auto;
}

.ai-attr-filter__row-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ai-attr-filter__row-bar {
    flex: 0 0 40px;
    height: 4px;
    margin-left: auto;
    overflow: hidden;
    background: var(--bg-canvas, #f8fafc);
    border-radius: 999px;
}

.ai-attr-filter__row-bar i {
    display: block;
    height: 100%;
    background: var(--brand-primary, #0d6efd);
    opacity: 0.5;
}

.ai-attr-filter__row-count {
    flex: 0 0 auto;
    min-width: 1.8rem;
    font-size: 0.7rem;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: var(--text-main, #495057);
    opacity: 0.65;
}

.ai-attr-filter__empty {
    padding: 0.9rem 0.6rem;
    font-size: 0.8rem;
    color: var(--text-main, #495057);
    opacity: 0.6;
}

.ai-attr-filter__footer {
    padding: 0.4rem 0.6rem;
    font-size: 0.7rem;
    color: var(--text-main, #495057);
    opacity: 0.65;
    border-top: 1px solid var(--border-default, #ced4da);
}

.ai-attr-filter__pills {
    display: flex;
    order: 1;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
}

.ai-attr-filter__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.15rem 0.15rem 0.15rem 0.6rem;
    font-size: 0.8rem;
    color: var(--brand-primary, #0d6efd);
    background: color-mix(in srgb, var(--brand-primary, #0d6efd) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--brand-primary, #0d6efd) 30%, transparent);
    border-radius: 999px;
}

.ai-attr-filter__pill-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.1rem;
    height: 1.1rem;
    padding: 0;
    font-size: 0.7rem;
    color: inherit;
    background: transparent;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
}

.ai-attr-filter__pill-remove:hover,
.ai-attr-filter__pill-remove:focus-visible {
    background: color-mix(in srgb, var(--brand-primary, #0d6efd) 20%, transparent);
    outline: none;
}

@media (max-width: 767.98px) {
    .ai-attr-filter__popover {
        position: fixed;
        top: auto;
        left: 0.75rem;
        right: 0.75rem;
        bottom: 0.75rem;
        width: auto;
        max-width: none;
        max-height: min(70vh, calc(100vh - 56px - var(--dashboard-restore-bar-height, 0px) - 1rem));
    }

    .ai-attr-filter__cats {
        flex: 0 0 140px;
        width: 140px;
    }
}
</style>
